import { create } from 'zustand';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

export const useIndiaPitchStore = create((set, get) => ({
  runId: null,
  status: null,       // RUNNING | SUCCESS | FAILED
  error: null,
  report: null,
  quality: null,
  loading: false,
  generating: false,
  pollInterval: null,
  allReports: [],
  allReportsLoading: false,

  playbooks: [],

  fetchPlaybooks: async () => {
    try {
      const { data } = await axios.get(`${API}/playbooks`);
      set({ playbooks: data.playbooks || [] });
    } catch { /* ignore */ }
  },

  generateReport: async (isin, playbook_id = 'equity_pitch', user_id = null) => {
    set({ generating: true, error: null, report: null, quality: null, status: null });
    try {
      const body = { isin, playbook_id };
      if (user_id) body.user_id = user_id;
      const { data } = await axios.post(`${API}/reports/generate`, body);
      set({ runId: data.run_id, status: 'RUNNING', generating: false });
      get().startPolling(data.run_id);
      return data;
    } catch (err) {
      set({ generating: false, error: err.response?.data?.detail || err.message });
      throw err;
    }
  },

  startPolling: (runId) => {
    const existing = get().pollInterval;
    if (existing) clearInterval(existing);

    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/reports/status/${runId}`);
        set({ status: data.status, error: data.error });

        if (data.status === 'SUCCESS') {
          get().stopPolling();
          get().fetchReport(runId);
        } else if (data.status === 'FAILED') {
          get().stopPolling();
        }
      } catch {
        // keep polling on transient errors
      }
    }, 3000);

    set({ pollInterval: interval });
  },

  stopPolling: () => {
    const interval = get().pollInterval;
    if (interval) clearInterval(interval);
    set({ pollInterval: null });
  },

  approvalStatus: null,
  approvalLoading: false,

  fetchReport: async (runId) => {
    set({ loading: true });
    try {
      const { data } = await axios.get(`${API}/reports/${runId}`);
      set({
        report: data.report,
        quality: data.quality,
        status: data.status,
        error: data.error,
        loading: false,
        approvalStatus: data.approval_status || 'draft',
      });
    } catch (err) {
      set({ loading: false, error: err.response?.data?.detail || err.message });
    }
  },

  submitForReview: async (runId) => {
    set({ approvalLoading: true });
    try {
      await axios.post(`${API}/reports/${runId}/submit-review`);
      set({ approvalStatus: 'pending_review', approvalLoading: false });
    } catch (err) {
      set({ approvalLoading: false });
    }
  },

  approveReport: async (runId, userId, notes = '') => {
    set({ approvalLoading: true });
    try {
      await axios.post(`${API}/reports/${runId}/approve`, { user_id: userId, notes });
      set({ approvalStatus: 'approved', approvalLoading: false });
    } catch (err) {
      set({ approvalLoading: false });
    }
  },

  rejectReport: async (runId, userId, notes = '') => {
    set({ approvalLoading: true });
    try {
      await axios.post(`${API}/reports/${runId}/reject`, { user_id: userId, notes });
      set({ approvalStatus: 'rejected', approvalLoading: false });
    } catch (err) {
      set({ approvalLoading: false });
    }
  },

  fetchLatestReport: async (isin) => {
    set({ loading: true, error: null });
    try {
      const { data } = await axios.get(`${API}/reports/latest/${isin}`);
      set({
        runId: data.run_id,
        report: data.report,
        quality: data.quality,
        status: data.status,
        loading: false,
        approvalStatus: data.approval_status || 'draft',
      });
      return data;
    } catch (err) {
      set({ loading: false, error: err.status === 404 ? null : (err.response?.data?.detail || err.message) });
      return null;
    }
  },

  fetchAllReports: async () => {
    set({ allReportsLoading: true });
    try {
      const { data } = await axios.get(`${API}/reports`, { params: { page_size: 50 } });
      set({ allReports: data.items || [], allReportsLoading: false });
    } catch {
      set({ allReportsLoading: false });
    }
  },

  downloadPdf: async (runId) => {
    try {
      const { data: html } = await axios.get(`${API}/reports/${runId}/pdf`, {
        responseType: 'text',
        headers: { Accept: 'text/html' },
      });
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-10000px;left:-10000px;width:850px;height:1200px;border:none';
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          setTimeout(() => document.body.removeChild(iframe), 1000);
        }, 300);
      };
      if (doc.readyState === 'complete') {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          setTimeout(() => document.body.removeChild(iframe), 1000);
        }, 300);
      }
    } catch {
      window.open(`${API}/reports/${runId}/pdf`, '_blank');
    }
  },

  reset: () => {
    get().stopPolling();
    set({
      runId: null, status: null, error: null,
      report: null, quality: null, loading: false, generating: false,
    });
  },
}));
